import { marketingAnalyticsEnv } from "./env";

const OPENAI_ADS_SDK_URL = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
const OPENAI_ADS_SCRIPT_ID = "rivetai-openai-ads-pixel";

export type OpenAiAdsMeasureEventName =
  | "order_created"
  | "registration_completed";

export type OpenAiAdsMeasureData = {
  type: "contents" | "customer_action";
  amount?: number;
  currency?: string;
};

export type OpenAiAdsMeasureOptions = {
  event_id?: string;
};

type OaiqCommand = (...args: unknown[]) => void;

type OaiqQueue = OaiqCommand & {
  q: unknown[][];
};

declare global {
  interface Window {
    oaiq?: OaiqQueue;
  }
}

let pixelInitialized = false;

const isOpenAiAdsDebugEnabled = (): boolean =>
  process.env.NODE_ENV !== "production";

export const isOpenAiAdsPixelConfigured = (): boolean =>
  Boolean(marketingAnalyticsEnv.openaiAdsPixelId);

const ensureOaiqQueue = (): OaiqQueue | null => {
  if (typeof window === "undefined") {
    return null;
  }

  if (window.oaiq) {
    return window.oaiq;
  }

  const queue: OaiqQueue = Object.assign(
    (...args: unknown[]) => {
      queue.q.push(args);
    },
    { q: [] as unknown[][] },
  );

  window.oaiq = queue;
  return queue;
};

const injectOpenAiAdsSdk = (): void => {
  if (typeof document === "undefined") {
    return;
  }
  if (document.getElementById(OPENAI_ADS_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement("script");
  script.id = OPENAI_ADS_SCRIPT_ID;
  script.async = true;
  script.src = OPENAI_ADS_SDK_URL;
  document.head.appendChild(script);
};

export const isOpenAiAdsPixelLoaded = (): boolean =>
  typeof document !== "undefined" &&
  document.getElementById(OPENAI_ADS_SCRIPT_ID) !== null;

/**
 * Load and initialize the OpenAI Ads Measurement Pixel when a pixel ID is configured.
 * Idempotent. Call only after analytics consent.
 */
export const ensureOpenAiAdsPixel = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  const pixelId = marketingAnalyticsEnv.openaiAdsPixelId;
  if (!pixelId) {
    return;
  }

  const oaiq = ensureOaiqQueue();
  if (!oaiq) {
    return;
  }

  injectOpenAiAdsSdk();

  if (pixelInitialized) {
    return;
  }

  oaiq("init", {
    pixelId,
    debug: isOpenAiAdsDebugEnabled(),
  });
  pixelInitialized = true;
};

export const measureOpenAiAdsEvent = (
  eventName: OpenAiAdsMeasureEventName,
  data: OpenAiAdsMeasureData,
  options?: OpenAiAdsMeasureOptions,
): void => {
  if (typeof window === "undefined") {
    return;
  }
  if (!isOpenAiAdsPixelConfigured()) {
    return;
  }

  ensureOpenAiAdsPixel();
  const oaiq = window.oaiq;
  if (!oaiq) {
    return;
  }

  if (options?.event_id) {
    oaiq("measure", eventName, data, { event_id: options.event_id });
    return;
  }

  oaiq("measure", eventName, data);
};

/** Test helper. Resets module init state between Vitest cases. */
export const resetOpenAiAdsPixelForTests = (): void => {
  pixelInitialized = false;
  if (typeof window !== "undefined") {
    delete window.oaiq;
  }
  if (typeof document !== "undefined") {
    document.getElementById(OPENAI_ADS_SCRIPT_ID)?.remove();
  }
};
